/**
 * ICONS — ported verbatim from the aesthetic reference (§8, L1747–1815) plus
 * the category and UI icons this app needs (REQUIRED_ICONS in contracts/taxonomy.ts).
 *
 * 24px grid, 1.5 stroke, round caps and joins. Drawn loosely, like the
 * brand book's landscape icons: hills, rings, paths, trees, water. Buildings
 * stand on a ground line; land and water borrow the contour. Nothing is
 * filled (the heart is filled by CSS only when it is kept).
 * Owner: S2.
 */
import type { CSSProperties, JSX } from "react";
import type { RequiredIcon } from "@/contracts/taxonomy";

const circ = (cx: number, cy: number, r: number): string =>
  `M${cx - r} ${cy}a${r} ${r} 0 1 0 ${2 * r} 0a${r} ${r} 0 1 0 ${-2 * r} 0`;

export const ICONS = {
  /* ── ported from the aesthetic (50) ─────────────────────────────────── */
  masterplan: ["M12 3.6c4.8 0 8.4 3.3 8.4 7.9 0 4.6-3.9 8.9-8.7 8.9-4.6 0-8.1-3.6-8.1-8.2 0-4.9 3.6-8.6 8.4-8.6Z", "M12.1 7.7c2.5 0 4.3 1.7 4.3 4 0 2.4-2 4.4-4.5 4.4-2.4 0-4.1-1.9-4.1-4.2 0-2.5 1.9-4.2 4.3-4.2Z", circ(12, 11.9, 0.6)],
  landscape: ["M2.8 18.6c2.7-4.4 4.9-6.6 6.8-6.6 1.9 0 3.3 1.6 4.5 3.4 1.3-2.1 2.8-3.6 4.5-3.6 1.4 0 2.6.9 3.6 2.4", "M2.8 18.6h18.4", circ(15.4, 6.8, 1.9)],
  homes: ["M4 20V10.6l5-4.1 5 4.1V20", "M14 20v-6.4l3-2.5 3 2.5V20", "M3 20h18", "M8 20v-4h2v4"],
  list: ["M9 7h11", "M9 12h11", "M9 17h8", circ(5, 7, 0.7), circ(5, 12, 0.7), circ(5, 17, 0.7)],
  gallery: ["M6 4.8h12a2 2 0 0 1 2 2v10.4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6.8a2 2 0 0 1 2-2Z", "M4.4 16.4l4.3-4.3 3.4 3.3 2.2-2.2 5.3 5.1", circ(15.3, 8.9, 1.3)],
  film: [circ(12, 12, 8.5), "M10.2 8.9v6.2l5-3.1Z"],
  walk: [circ(13.2, 4.6, 1.5), "M13.4 8.4l-1.8 4.9 2.8 2.6.9 4.6", "M11.6 13.3l-2.4 6.4", "M13.4 8.4l2.6 2.9 2.6.5", "M13.4 8.4l-2.9 1.4-1.3 2.9"],
  heart: ["M12 19.6s-7.4-4.4-7.4-9.8A4.2 4.2 0 0 1 12 7.2a4.2 4.2 0 0 1 7.4 2.6c0 5.4-7.4 9.8-7.4 9.8Z"],
  globe: [circ(12, 12, 8.5), "M3.6 12h16.8", "M12 3.5c2.3 2.4 3.5 5.2 3.5 8.5s-1.2 6.1-3.5 8.5c-2.3-2.4-3.5-5.2-3.5-8.5s1.2-6.1 3.5-8.5Z"],
  close: ["M6.5 6.5l11 11", "M17.5 6.5l-11 11"],
  chevL: ["M14.5 6l-6 6 6 6"],
  chevR: ["M9.5 6l6 6-6 6"],
  chevD: ["M6 9.5l6 6 6-6"],
  chevU: ["M6 14.5l6-6 6 6"],
  plus: ["M12 5.5v13", "M5.5 12h13"],
  minus: ["M5.5 12h13"],
  recenter: [circ(12, 12, 5.6), circ(12, 12, 1), "M12 3.4v3", "M12 17.6v3", "M3.4 12h3", "M17.6 12h3"],
  soundOff: ["M4 9.6h3.4L12 5.6v12.8l-4.6-4H4Z", "M16 9.5l4.5 5", "M20.5 9.5l-4.5 5"],
  soundOn: ["M4 9.6h3.4L12 5.6v12.8l-4.6-4H4Z", "M15.6 9.2c.9.8 1.4 1.8 1.4 2.8s-.5 2-1.4 2.8", "M18 6.8c1.6 1.4 2.5 3.2 2.5 5.2s-.9 3.8-2.5 5.2"],
  expand: ["M4.5 9V4.5H9", "M15 4.5h4.5V9", "M19.5 15v4.5H15", "M9 19.5H4.5V15"],
  share: ["M12 3.8v10.7", "M8.2 7.4 12 3.6l3.8 3.8", "M5.6 12.6v4.9a2 2 0 0 0 2 2h8.8a2 2 0 0 0 2-2v-4.9"],
  download: ["M12 4v10.8", "M7.6 10.6 12 15l4.4-4.4", "M5 19.4h14"],
  phone: ["M6.6 3.9l2.6-.3 1.6 4-1.9 1.4a11 11 0 0 0 6.1 6.1l1.4-1.9 4 1.6-.3 2.6a2 2 0 0 1-2.1 1.8C11 18.8 5.2 13 4.8 6a2 2 0 0 1 1.8-2.1Z"],
  compare: ["M5.5 4.8h4v14.4h-4a1 1 0 0 1-1-1V5.8a1 1 0 0 1 1-1Z", "M14.5 4.8h4a1 1 0 0 1 1 1v12.4a1 1 0 0 1-1 1h-4Z", "M12 3v18"],
  filter: ["M4 7h16", "M7 12h10", "M10 17h4"],
  search: [circ(10.8, 10.8, 6.3), "M15.6 15.6l4.4 4.4"],
  sun: [circ(12, 12, 3.8), "M12 2.8v2", "M12 19.2v2", "M2.8 12h2", "M19.2 12h2", "M5.5 5.5l1.4 1.4", "M17.1 17.1l1.4 1.4", "M5.5 18.5l1.4-1.4", "M17.1 6.9l1.4-1.4"],
  moon: ["M19.4 14.6A7.9 7.9 0 0 1 9.4 4.6a8 8 0 1 0 10 10Z"],
  sunrise: ["M3 18.5h18", "M6.6 18.5a5.4 5.4 0 0 1 10.8 0", "M12 6.4v2.4", "M5.4 11.2l1.6 1.2", "M18.6 11.2 17 12.4"],
  sunset: ["M3 18.5h18", "M7 18.5a5 5 0 0 1 10 0", "M12 6.6v4.6", "M9.8 9.2 12 11.4l2.2-2.2"],
  layers: ["M12 4.2 3.8 8.6 12 13l8.2-4.4Z", "M3.8 12.6 12 17l8.2-4.4", "M3.8 16.4 12 20.8l8.2-4.4"],
  elevation: ["M3.4 18.6 9 10.6l3.8 5.2 2.3-3 5.5 5.8", "M3.4 18.6h17.2", "M16.8 4v5", "M14.8 6l2-2 2 2"],
  tree: ["M12 21v-6", "M12 15c-3.9 0-6.5-2.3-6.5-5.4 0-3.2 2.8-6.1 6.5-6.1s6.5 2.9 6.5 6.1c0 3.1-2.6 5.4-6.5 5.4Z", "M12 15l-2.6-2.4", "M12 13.4l2.4-2"],
  waves: ["M3 9.5c1.5 0 1.5-1.4 3-1.4s1.5 1.4 3 1.4 1.5-1.4 3-1.4 1.5 1.4 3 1.4 1.5-1.4 3-1.4 1.5 1.4 3 1.4", "M3 14.5c1.5 0 1.5-1.4 3-1.4s1.5 1.4 3 1.4 1.5-1.4 3-1.4 1.5 1.4 3 1.4 1.5-1.4 3-1.4 1.5 1.4 3 1.4"],
  path: ["M6.4 20.6c.9-3.4 9.8-3 9.8-6.6 0-3.3-8.4-2.4-8.4-5.9C7.8 5.4 12.4 4 15.8 3.4", "M9.8 20.8c.5-1.3 1.9-1.9 3.8-2.4"],
  info: [circ(12, 12, 8.5), "M12 11v5.3", "M12 7.8v.3"],
  bench: ["M4 11.4h16", "M4 14.8h16", "M6.2 14.8v4.4", "M17.8 14.8v4.4", "M5.4 11.4 6 7.2h12l.6 4.2"],
  bridge: ["M2.6 15.6C5.6 11.2 8.6 9 12 9s6.4 2.2 9.4 6.6", "M2.6 15.6h18.8", "M7 15.6v-3.6", "M12 15.6V9", "M17 15.6v-3.6", "M3.6 19c1.4 0 1.4-1 2.8-1s1.4 1 2.8 1"],
  floorplan: ["M4.5 4.5h15v15h-15Z", "M4.5 12h6", "M14 12h5.5", "M12 4.5v4", "M12 12v7.5"],
  check: ["M5.5 12.5l4.2 4 8.8-9"],
  sort: ["M8 5v14", "M5 16l3 3 3-3", "M16 19V5", "M13 8l3-3 3 3"],
  play: ["M8.5 5.6v12.8L18.6 12Z"],
  pause: ["M9 5.5v13", "M15 5.5v13"],
  pin: ["M12 20.8s-6.4-6-6.4-10.8a6.4 6.4 0 0 1 12.8 0c0 4.8-6.4 10.8-6.4 10.8Z", circ(12, 10, 2.2)],
  clock: [circ(12, 12, 8.5), "M12 7.6V12l3 2"],
  window: ["M5.5 4.5h13v15h-13Z", "M12 4.5v15", "M5.5 12h13"],
  spin360: ["M4 12c0-2 3.6-3.6 8-3.6s8 1.6 8 3.6-3.6 3.6-8 3.6c-1.4 0-2.8-.2-4-.5", "M9.6 13.6 7.8 15.2l1.8 1.6"],
  still: [circ(12, 12, 3.2), "M4.5 8a2 2 0 0 1 2-2h2l1.4-1.8h4.2L15.5 6h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-11a2 2 0 0 1-2-2Z"],
  calendar: ["M5.5 6h13a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1h-13a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1Z", "M4.5 10h15", "M8.5 4v4", "M15.5 4v4"],
  arrowUpRight: ["M7.5 16.5l9-9", "M9 7.5h7.5V15"],

  /* ── category icons ─────────────────────────────────────────────────── */
  // A road running to the horizon between two low hills.
  road: ["M4 20.6c2-4.7 4.3-9.4 6.9-14", "M20 20.6c-1.8-4.8-3.9-9.5-6.6-14", "M12.1 20.2v-2.6", "M12.1 14.8v-1.9", "M12.1 10.4v-1.2", "M2.8 8.6c2.6-1.4 5.2-2 8.1-2", "M13.4 6.6c2.9 0 5.5.6 7.8 2"],
  // A road that closes on itself: two loose edges and the dashed centre line.
  ringRoad: ["M12.2 3c5 0 8.8 3.9 8.8 9 0 5-4 9-9 9-5 .1-9-3.9-9-8.9C3 7 7.2 3 12.2 3Z", "M12.1 8.4c2.1 0 3.6 1.5 3.6 3.6 0 2.1-1.6 3.7-3.7 3.7-2 0-3.6-1.6-3.6-3.7 0-2 1.6-3.6 3.7-3.6Z", "M18.1 13.1A6.2 6.2 0 0 1 17.1 15.5", "M14.1 17.8A6.2 6.2 0 0 1 11.6 18.2", "M8 16.7A6.2 6.2 0 0 1 6.4 14.7", "M5.9 10.9A6.2 6.2 0 0 1 6.9 8.5", "M9.9 6.2A6.2 6.2 0 0 1 12.4 5.8", "M16 7.3A6.2 6.2 0 0 1 17.6 9.3"],
  plane: ["M19.1 4.9Q18.2 4.3 16.9 5.5L13.7 8.8L5.5 6.4L4.3 7.7L10.8 11.6L8.4 14L5.3 13.1L4.5 13.9L7.1 15.5Q7.6 16.4 8.5 16.9L10.1 19.5L10.9 18.7L10 15.6L12.4 13.2L16.3 19.7L17.6 18.5L15.2 10.3L18.5 7.1Q19.7 5.8 19.1 4.9Z"],
  train: ["M5.2 15.2V8.8c0-3 3-5 6.8-5s6.8 2 6.8 5v6.4c0 1.1-.9 2-2 2H7.2c-1.1 0-2-.9-2-2Z", "M7.4 9.2c1.4-.5 3-.8 4.6-.8s3.2.3 4.6.8v2.6H7.4Z", circ(8.8, 14.4, 0.5), circ(15.2, 14.4, 0.5), "M8.4 17.2 6 20.8", "M15.6 17.2l2.4 3.6", "M6.9 19.4h10.2"],
  school: ["M3 20.2h18", "M4.8 20.2v-8.4L12 7l7.2 4.8v8.4", "M12 7V3.2", "M12 3.4c1-.6 1.9.5 2.9-.1v2c-1 .6-1.9-.5-2.9.1", "M10.2 20.2v-3.4a1.8 1.8 0 0 1 3.6 0v3.4", circ(12, 12.2, 1.4)],
  hospital: ["M3 20.2h18", "M5.4 20.2V7.6c0-1 .8-1.8 1.8-1.8h9.6c1 0 1.8.8 1.8 1.8v12.6", "M12 8.8v4.6", "M9.7 11.1h4.6", "M10.2 20.2v-3.2h3.6v3.2"],
  bag: ["M5.6 8.4h12.8l.8 10.8c.1.9-.6 1.6-1.5 1.6H6.3c-.9 0-1.6-.7-1.5-1.6Z", "M9 11V7.2c0-1.9 1.3-3.4 3-3.4s3 1.5 3 3.4V11"],
  city: ["M3 20.2h18", "M4.6 20.2v-7.4h4", "M8.6 20.2V6.4l4.8-2.2v16", "M13.4 20.2V9.8h5.4v10.4", "M10.8 9.2v.9", "M10.8 12.6v.9", "M10.8 16v.9", "M16 13v.9", "M16 16.2v.9"],
  hotel: ["M3.4 5.8v14.4", "M3.4 15.6h17.2v4.6", "M20.6 15.6v-2.2c0-1.6-1.3-2.8-2.8-2.8h-7.4v5", circ(7.2, 12.8, 1.7)],
  briefcase: ["M4.4 8.2h15.2c.8 0 1.4.6 1.4 1.4v8.8c0 .9-.7 1.6-1.6 1.6H4.6c-.9 0-1.6-.7-1.6-1.6V9.6c0-.8.6-1.4 1.4-1.4Z", "M9 8.2V6.4c0-.9.7-1.6 1.6-1.6h2.8c.9 0 1.6.7 1.6 1.6v1.8", "M3 12.6c2.9 1.2 5.9 1.7 9 1.7s6.1-.5 9-1.7", "M12 13.4v1.8"],
  factory: ["M3 20.2h18", "M4 20.2V5.6h2.8v5.8l4.4-3v3l4.4-3v3l4.4-3v11.8", "M5.4 3.6c.8-.8 1.9-.9 2.8-.3.9.6 2 .5 2.8-.3", "M8.6 15.2h1.8", "M13 15.2h1.8", "M17.2 15.2h.8"],
  tower: ["M3 20.2h18", "M9 20.2V5.4c0-.8.6-1.4 1.4-1.4h3.2c.8 0 1.4.6 1.4 1.4v14.8", "M9 8.4h6", "M9 11.8h6", "M9 15.2h6", "M4.8 20.2v-5.4H9", "M18.2 20.2v-2.6", "M18.2 17.6c-1.3 0-2.2-.9-2.2-2.1 0-1.3 1-2.4 2.2-2.4s2.2 1.1 2.2 2.4c0 1.2-.9 2.1-2.2 2.1Z"],
  // A soft site boundary, drawn like a contour, with a home inside.
  township: ["M3.4 12.6c-.2-3.4 1.6-6.4 4.8-7.8 3.6-1.6 7.4-1.4 10.2.8 2.6 2 3 5.4 1.6 8.4-1.2 2.4-.6 4.4-3.2 5.4-3.4 1.2-7.6.8-10.4-.8-1.8-1.2-2.8-3.2-3-6Z", "M8.4 15.4V12l3.8-3 3.8 3v3.4", "M7.2 15.4h10"],
  sprout: ["M12 20.4v-8.6", "M12 12.8c-.3-3.2-2.6-5-6.4-5 .1 3.3 2.5 5.1 6.4 5Z", "M12 10.8c.2-3.8 2.6-6 6.6-6 0 3.9-2.5 6.1-6.6 6Z", "M6.2 20.4c3.8-1.1 7.8-1.1 11.6 0"],

  /* ── UI icons ───────────────────────────────────────────────────────── */
  car: ["M5 16.6H3.9c-.5 0-.9-.4-.9-.9v-2.4c0-.8.5-1.4 1.2-1.6l2.6-.8 2.2-3.2c.4-.6 1.1-1 1.8-1h4.4c.7 0 1.3.3 1.7.9l2 3.1 1.2.4c.6.2.9.7.9 1.3v3.3c0 .5-.4.9-.9.9H19", "M8.8 16.6h6.4", "M6.8 10.9h12", "M12.8 6.8v4.1", circ(6.9, 16.6, 1.9), circ(17.1, 16.6, 1.9)],
  route: [circ(5.6, 18.2, 1.6), circ(18.4, 5.8, 1.6), "M7.2 18.2c3.4.2 9-.1 9-3 0-3.3-8.8-2.1-8.8-5.5 0-2.4 3.6-3.1 9.4-3.9"],
  compass: [circ(12, 12, 8.5), "M12 6.4l2.4 5.6-2.4 5.6-2.4-5.6Z", "M9.6 12h4.8"],
  target: [circ(12, 12, 8.4), circ(12, 12, 4.6), circ(12, 12, 1), "M12.8 11.2l6.4-6.4", "M19.2 4.8l.4-2", "M19.2 4.8l2-.4"],
  university: ["M2.8 9.2 12 4.8l9.2 4.4-9.2 4.4Z", "M6.6 11.4v4.2c1.5 1.4 3.3 2.1 5.4 2.1s3.9-.7 5.4-2.1v-4.2", "M21.2 9.2v5.4"],
  mapPin: ["M12 17.4s-5.2-4.8-5.2-8.8a5.2 5.2 0 0 1 10.4 0c0 4-5.2 8.8-5.2 8.8Z", circ(12, 8.6, 1.8), "M8.2 16.4c-2.3.4-3.8 1.1-3.8 1.9 0 1.2 3.4 2.2 7.6 2.2s7.6-1 7.6-2.2c0-.8-1.5-1.5-3.8-1.9"],
  external: ["M18.6 13.2v4.6c0 1-.8 1.8-1.8 1.8H6.2c-1 0-1.8-.8-1.8-1.8V7.2c0-1 .8-1.8 1.8-1.8h4.6", "M14.2 4.4h5.4v5.4", "M19.6 4.4l-8.2 8.2"],
} satisfies Record<string, string[]>;

export type IconName = keyof typeof ICONS;
export const ICON_NAMES: IconName[] = Object.keys(ICONS) as IconName[];

// Compile-time guarantee that every icon the data and UI rely on exists.
type MissingRequired = Exclude<RequiredIcon, IconName>;
const _allRequired: [MissingRequired] extends [never] ? true : MissingRequired = true;
void _allRequired;

/** Narrow a data-driven string to a known icon name. */
export const isIconName = (n: string): n is IconName => Object.prototype.hasOwnProperty.call(ICONS, n);

export interface IconProps {
  /** Any string compiles; unknown names draw "info". */
  name: IconName | (string & {});
  size?: number;
  stroke?: number;
  className?: string;
  style?: CSSProperties;
  title?: string;
}

export function Icon({ name, size = 20, stroke = 1.5, className, style, title }: IconProps): JSX.Element {
  const d: readonly string[] = isIconName(name) ? ICONS[name] : ICONS.info;
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={title ? undefined : true}
      role={title ? "img" : undefined}
      className={className}
      style={style}
    >
      {title && <title>{title}</title>}
      {d.map((p, i) => (
        <path key={i} d={p} />
      ))}
    </svg>
  );
}
